import React, { useState, useEffect, useRef } from 'react'
import { Music, Search, Download, Loader2, Sparkles, Plus, GripVertical, Library, Wand2, X } from 'lucide-react'
import { musicApi } from '../api/client'
import type { MusicSong, MusicTrack } from '../types'

interface MusicPanelProps {
  musicTrack: MusicTrack | null
  onUseTrack: (track: MusicTrack) => void
  onClearTrack: () => void
  /** Place a track on the timeline's Music lane. */
  onAddToTimeline?: (clip: { url: string; text: string; duration?: number }) => void
}

interface MusicGen { job_id: string; url: string; prompt: string; duration: number; genre?: string }

const SEARCH_DEBOUNCE_MS = 400
const GEN_HISTORY_KEY = 'legit2clip_music_history'

export function MusicPanel({ musicTrack, onUseTrack, onClearTrack, onAddToTimeline }: MusicPanelProps) {
  const [view, setView] = useState<'library' | 'mine'>('library')
  const [query, setQuery] = useState('')
  const [genre, setGenre] = useState('')
  const [songs, setSongs] = useState<MusicSong[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [genres, setGenres] = useState<{ genre: string; count: number }[]>([])
  const searchSeq = useRef(0)

  // ── Music generation ──
  const [showGen, setShowGen] = useState(false)
  const [genPrompt, setGenPrompt] = useState('')
  const [genDuration, setGenDuration] = useState(20)
  const [genJobId, setGenJobId] = useState<string | null>(null)
  const [genStatus, setGenStatus] = useState('')
  const [genError, setGenError] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [genHistory, setGenHistory] = useState<MusicGen[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => { musicApi.getGenres().then(d => setGenres(d.genres || [])).catch(() => {}) }, [])

  useEffect(() => {
    try { const raw = localStorage.getItem(GEN_HISTORY_KEY); if (raw) setGenHistory(JSON.parse(raw)) } catch {}
    setLoaded(true)
  }, [])
  useEffect(() => { if (loaded) { try { localStorage.setItem(GEN_HISTORY_KEY, JSON.stringify(genHistory)) } catch {} } }, [genHistory, loaded])

  useEffect(() => {
    if (!genJobId) return
    let cancelled = false
    const poll = async () => {
      try {
        const data = await musicApi.getGenStatus(genJobId)
        if (cancelled) return
        if (data.ready) {
          setGenHistory(prev => [{ job_id: genJobId, url: musicApi.getGenDownloadUrl(genJobId), prompt: genPrompt, duration: genDuration }, ...prev])
          setIsGenerating(false); setGenJobId(null); setGenStatus('')
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          setGenError(data.error_message || 'Generation failed'); setIsGenerating(false); setGenJobId(null); setGenStatus('')
        } else {
          const pct = typeof data.progress === 'number' ? ` — ${Math.round(data.progress)}%` : ''
          setGenStatus(`${data.status || 'processing'}${pct}`)
        }
      } catch {}
    }
    poll()
    const interval = window.setInterval(poll, 3000)
    return () => { cancelled = true; window.clearInterval(interval) }
  }, [genJobId])

  const handleGenerate = async () => {
    const prompt = genPrompt.trim()
    if (!prompt || isGenerating) return
    setGenError(''); setIsGenerating(true); setGenStatus('Submitting...'); setView('mine')
    try {
      const data = await musicApi.generate({ prompt, duration: genDuration })
      if (!data.job_id) throw new Error('No job id returned')
      setGenJobId(data.job_id); setGenStatus('Queued...'); setShowGen(false); setGenPrompt('')
    } catch (error: any) {
      setGenError(error?.response?.data?.error || error?.message || 'Failed to start generation'); setIsGenerating(false); setGenStatus('')
    }
  }

  // Library search (words/style + genre). Only runs while browsing the library.
  useEffect(() => {
    if (view !== 'library') return
    if (!query.trim() && !genre.trim()) { searchSeq.current++; setSongs([]); setHasSearched(false); setIsLoading(false); setError(''); return }
    const seq = ++searchSeq.current
    const timer = setTimeout(async () => {
      setIsLoading(true); setError('')
      try {
        const data = await musicApi.searchSongs({ q: query.trim(), genre: genre.trim(), limit: 40 })
        if (seq !== searchSeq.current) return
        setSongs(data.songs || []); setHasSearched(true)
      } catch (err: any) {
        if (seq !== searchSeq.current) return
        setError(err?.response?.data?.error || err?.message || 'Music search failed'); setSongs([]); setHasSearched(true)
      } finally { if (seq === searchSeq.current) setIsLoading(false) }
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, genre, view])

  const fmtDur = (s?: number) => (s == null || isNaN(s)) ? '' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  const totalTracks = genres.reduce((a, g) => a + g.count, 0)

  const place = (clip: { url: string; text: string; duration?: number; filename?: string; genre?: string }) => {
    onAddToTimeline?.({ url: clip.url, text: clip.text, duration: clip.duration })
    onUseTrack({ title: clip.text, filename: clip.filename || 'track', stream_path: clip.url, duration: clip.duration, genre: clip.genre })
  }
  const dragProps = (clip: { url: string; text: string; duration?: number }) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => { e.dataTransfer.setData('application/x-music-clip', JSON.stringify(clip)); e.dataTransfer.effectAllowed = 'copy' },
  })

  const q = query.trim().toLowerCase()
  const mine = q ? genHistory.filter(g => g.prompt.toLowerCase().includes(q)) : genHistory

  const trackActions = (clip: { url: string; text: string; duration?: number; filename?: string; genre?: string }) => (
    <div className="flex items-center gap-1.5 mt-1">
      <button onClick={() => place(clip)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-fuchsia-600/80 hover:bg-fuchsia-600 text-white text-[11px] font-medium"><Plus size={11} /> Add to Music track</button>
      <span {...dragProps(clip)} title="Drag onto the Music track" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-600 text-gray-400 hover:text-white text-[11px] cursor-grab active:cursor-grabbing select-none"><GripVertical size={11} /> drag</span>
    </div>
  )

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-600 flex-1 min-h-0 flex flex-col">
      <div className="p-3 border-b border-gray-600 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2"><Music className="text-gray-400" size={16} /><h3 className="text-sm font-semibold text-white">Music</h3></div>
          <button onClick={() => { setView('mine'); setShowGen(true) }} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-indigo-600/80 hover:bg-indigo-600 text-white"><Wand2 size={12} /> Generate</button>
        </div>

        {/* View switch */}
        <div className="flex items-center bg-gray-900 rounded-lg p-0.5 text-xs mb-2">
          <button onClick={() => setView('library')} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md transition-colors ${view === 'library' ? 'bg-fuchsia-600 text-white' : 'text-gray-400 hover:text-white'}`}><Library size={13} /> Free Library</button>
          <button onClick={() => setView('mine')} className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md transition-colors ${view === 'mine' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}><Sparkles size={13} /> My Music{genHistory.length ? ` (${genHistory.length})` : ''}</button>
        </div>

        {/* Search by words / style (both views) */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
          <input type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={view === 'library' ? `Search ${totalTracks ? totalTracks.toLocaleString() : '20,000+'} tracks by words or style...` : 'Filter your tracks...'}
            className="w-full bg-gray-900 border border-gray-600 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
        </div>

        {/* Genre filter (library only) */}
        {view === 'library' && (
          <div className="flex flex-wrap gap-1 mt-2">
            <button onClick={() => setGenre('')} className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${genre === '' ? 'bg-fuchsia-600 border-fuchsia-500 text-white' : 'border-gray-600 text-gray-400 hover:text-white'}`}>All</button>
            {genres.slice(0, 18).map((g) => (
              <button key={g.genre} onClick={() => setGenre(genre === g.genre ? '' : g.genre)}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${genre === g.genre ? 'bg-fuchsia-600 border-fuchsia-500 text-white' : 'border-gray-600 text-gray-400 hover:text-white'}`}>{g.genre}</button>
            ))}
          </div>
        )}

        {/* Generate form (My Music) */}
        {view === 'mine' && showGen && (
          <div className="mt-2 rounded-lg border border-indigo-600/30 bg-indigo-950/20 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5"><Sparkles className="text-indigo-400" size={13} /><span className="text-xs font-medium text-indigo-200">Generate a track</span></div>
              <button onClick={() => setShowGen(false)} className="text-gray-500 hover:text-white"><X size={12} /></button>
            </div>
            <textarea value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} rows={2} maxLength={800}
              placeholder="Describe the music... ('warm lofi study beat, mellow, 90bpm, no vocals')" disabled={isGenerating}
              className="w-full bg-gray-900 border border-gray-600 rounded-lg px-2.5 py-1.5 text-xs text-white resize-none placeholder-gray-500 focus:outline-none focus:border-indigo-500 disabled:opacity-50" />
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] text-gray-400 shrink-0">Length</span>
              <input type="range" min={5} max={60} step={5} value={genDuration} onChange={(e) => setGenDuration(Number(e.target.value))} disabled={isGenerating} className="flex-1 accent-indigo-500" />
              <span className="text-[10px] text-gray-300 font-mono w-8 text-right shrink-0">{genDuration}s</span>
            </div>
            <button onClick={handleGenerate} disabled={!genPrompt.trim() || isGenerating}
              className="w-full mt-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-40 flex items-center justify-center gap-2">✨ Generate</button>
            {genError && <p className="text-[11px] text-red-300 mt-1.5">{genError}</p>}
          </div>
        )}

        {musicTrack && (
          <div className="flex items-center gap-1.5 mt-2 px-2 py-1 rounded-lg bg-indigo-900/40 border border-indigo-600/40">
            <Music className="text-indigo-400 shrink-0" size={12} />
            <span className="text-xs text-indigo-200 truncate flex-1">Export bed: {musicTrack.title}</span>
            <button onClick={onClearTrack} className="text-indigo-400 hover:text-white shrink-0" title="Remove music bed"><X size={12} /></button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {view === 'mine' ? (
          <>
            {isGenerating && (
              <div className="flex items-center gap-2 rounded-lg bg-gray-900/60 border border-gray-600/40 p-2 text-xs text-amber-300"><Loader2 className="animate-spin" size={12} />{genStatus || 'Generating your track...'}</div>
            )}
            {mine.length === 0 && !isGenerating ? (
              <div className="text-center py-8 text-gray-500">
                <Sparkles className="mx-auto mb-2" size={24} />
                <p className="text-xs">{query ? 'No generated tracks match that.' : 'Your generated music will appear here.'}</p>
                <button onClick={() => setShowGen(true)} className="mt-2 text-[11px] px-2.5 py-1 rounded-md bg-indigo-600/80 hover:bg-indigo-600 text-white">✨ Generate a track</button>
              </div>
            ) : mine.map((g) => {
              const clip = { url: g.url, text: `Generated · ${g.prompt.slice(0, 34)}`, duration: g.duration, filename: `music-${g.job_id.slice(0, 8)}.wav` }
              return (
                <div key={g.job_id} className="rounded-lg p-2 border bg-gray-900/60 border-gray-600/40">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-white truncate flex-1">🎵 {g.prompt}</p>
                    <a href={g.url} download={`music-${g.job_id.slice(0, 8)}.wav`} className="p-1 text-gray-400 hover:text-white shrink-0" title="Download"><Download size={13} /></a>
                  </div>
                  <audio controls preload="none" src={g.url} className="w-full h-8 mt-1.5" />
                  {trackActions(clip)}
                </div>
              )
            })}
          </>
        ) : (
          <>
            <p className="text-[11px] text-gray-500 px-1 pb-1">🎧 Royalty-free — cleared for use in your videos.</p>
            {isLoading && (<div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-xs"><Loader2 className="animate-spin" size={14} />Searching...</div>)}
            {!isLoading && error && (<div className="bg-red-900/40 border border-red-600/40 rounded-lg px-3 py-2 text-xs text-red-200">{error}</div>)}
            {!isLoading && !error && !hasSearched && (
              <div className="text-center py-8 text-gray-500">
                <Library className="mx-auto mb-2" size={24} />
                <p className="text-xs">Pick a genre or search {totalTracks ? totalTracks.toLocaleString() : '20,000+'} free tracks by words or style.</p>
              </div>
            )}
            {!isLoading && !error && hasSearched && songs.length === 0 && (<div className="text-center py-8 text-gray-500"><p className="text-xs">No tracks matched — try a different search or genre.</p></div>)}
            {!isLoading && songs.map((song) => {
              const streamUrl = musicApi.getStreamUrl(song.filename)
              const clip = { url: streamUrl, text: song.title, duration: song.duration, filename: song.filename, genre: song.genre }
              return (
                <div key={song.share_id || song.filename} className="rounded-lg p-2 border bg-gray-900/60 border-gray-600/40 hover:border-gray-500 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-white truncate">{song.title}</p>
                      <p className="text-[10px] text-gray-400">{song.genre || 'unknown'}{fmtDur(song.duration) && ` • ${fmtDur(song.duration)}`}</p>
                    </div>
                    <a href={streamUrl} download={song.filename} className="p-1 text-gray-400 hover:text-white shrink-0" title="Download track"><Download size={13} /></a>
                  </div>
                  <audio controls preload="none" src={streamUrl} className="w-full h-8 mt-1.5" />
                  {trackActions(clip)}
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}
